import React from 'react';
import { getLogoSrc } from '../utils/logoHelper';
import { formatGameDate, formatGameTime } from '../utils/dateFormatter';
import api from '../services/api';

const API_BASE_URL = api.defaults.baseURL.replace('/api', '');
const W = 1080;
const H = 1080;
const MAX_H = 1350;

function GraphicTryoutPoster({
  graphicData,
  teamSport,
  teamColors,
  backgroundStyle = 'linear-gradient',
  homeTeamLogo,
  showWatermark = true,
  dateFormat = 'full',
  heroFont = 'Bebas Neue',
  heroFontSize = 160,
  accentBlockColor = null,
  detailTextColor = null,
}) {
  const {
    homeTeam, ageGroup, tryoutDate, tryoutDate2,
    tryoutTime, location, contact, additionalInfo, backgroundPhoto,
  } = graphicData;
  const bgImageScale = (graphicData.bgImageScale ?? 100) / 100;
  const bgImageX = graphicData.bgImageX ?? 50;
  const bgImageY = graphicData.bgImageY ?? 50;

  const accent = accentBlockColor || teamColors.primary;
  const detailColor = detailTextColor || '#ffffff';

  const isLightColor = (color) => {
    if (!color || color === 'null') return false;
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    return (r * 299 + g * 587 + b * 114) / 1000 > 155;
  };

  const getBackground = () => {
    const p = teamColors.primary;
    const s = teamColors.secondary;
    switch (backgroundStyle) {
      case 'radial-gradient': return `radial-gradient(circle at center, ${p} 0%, ${s} 100%)`;
      case 'diagonal-gradient': return `linear-gradient(45deg, ${p} 0%, ${s} 100%)`;
      case 'split-two-tone': return `linear-gradient(180deg, ${p} 0%, ${p} 50%, ${s} 50%, ${s} 100%)`;
      case 'solid-primary': return p;
      case 'solid-secondary': return s;
      default: return `linear-gradient(135deg, ${p} 0%, ${s} 100%)`;
    }
  };

  const tryoutsSize = Math.max(120, Math.min(heroFontSize, 220));
  const teamNameSize = (homeTeam || 'TEAM NAME').length > 14 ? 72 : (homeTeam || 'TEAM NAME').length > 9 ? 88 : 108;

  const logoCircleStyle = {
    width: 160,
    height: 160,
    borderRadius: '50%',
    background: '#FFFFFF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    flexShrink: 0,
  };

  const dotStyle = {
    width: 10,
    height: 10,
    borderRadius: '50%',
    background: detailColor,
    flexShrink: 0,
  };

  return (
    <div
      id="graphic-tryout"
      style={{
        width: W,
        minHeight: H,
        maxHeight: MAX_H,
        position: 'relative',
        overflow: 'hidden',
        height: 'auto',
        background: getBackground(),
        borderRadius: 16,
        fontFamily: 'Barlow, sans-serif',
      }}
    >
      {/* Background Photo */}
      {backgroundPhoto && (
        <>
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: W,
              height: H,
              overflow: 'hidden',
              zIndex: 0,
            }}
          >
            <img
              src={backgroundPhoto}
              alt=""
              crossOrigin="anonymous"
              style={{
                position: 'absolute',
                top: `${bgImageY}%`,
                left: `${bgImageX}%`,
                transform: `translate(-50%, -50%) scale(${bgImageScale})`,
                transformOrigin: 'center center',
                width: 'auto',
                height: 'auto',
                minWidth: '100%',
                minHeight: '100%',
                maxWidth: 'none',
              }}
            />
          </div>
          <div style={{
            position: 'absolute', inset: 0,
            background: 'linear-gradient(180deg, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.3) 50%, rgba(0,0,0,0.7) 100%)',
            zIndex: 1,
          }} />
        </>
      )}

      {/* Content */}
      <div style={{
        position: 'relative', zIndex: 2,
        width: '100%', height: '100%',
        display: 'flex', flexDirection: 'column',
        padding: '72px 80px 60px',
      }}>

        {/* Top — Logo + Team Name */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 40, marginBottom: 48 }}>
          <div style={logoCircleStyle}>
            {homeTeamLogo ? (
              <img
                src={getLogoSrc(homeTeamLogo, API_BASE_URL)}
                alt={homeTeam}
                style={{ width: 'auto', height: 'auto', maxWidth: '80%', maxHeight: '80%' }}
              />
            ) : (
              <span style={{ fontSize: 80 }}>
                {teamSport === 'Softball' ? '🥎' : '⚾'}
              </span>
            )}
          </div>
          <div>
            <div style={{
              fontFamily: 'Bebas Neue, sans-serif',
              fontSize: teamNameSize,
              fontWeight: 900,
              color: '#ffffff',
              textShadow: '0 2px 12px rgba(0,0,0,0.5)',
              lineHeight: 1,
              letterSpacing: 3,
            }}>
              {homeTeam || 'TEAM NAME'}
            </div>
          </div>
        </div>

        {/* TRYOUTS Hero Text */}
        <div style={{
          fontFamily: heroFont,
          fontSize: tryoutsSize,
          fontWeight: 900,
          color: '#ffffff',
          textShadow: `0 4px 24px rgba(0,0,0,0.4)`,
          lineHeight: 0.9,
          letterSpacing: 8,
          marginBottom: 32,
        }}>
          TRYOUTS
        </div>

        {/* Age Group Badge */}
        <div style={{ marginBottom: 48 }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            width: 'fit-content',
            gap: 16,
            background: accent,
            padding: '16px 40px',
            borderRadius: 60,
          }}>
            <span style={{
              fontFamily: 'Bebas Neue, sans-serif',
              fontSize: 28,
              color: isLightColor(accent) ? '#1a202c' : '#ffffff',
              letterSpacing: 4,
            }}>
              LOOKING FOR:
            </span>
            <span style={{
              fontFamily: 'Bebas Neue, sans-serif',
              fontSize: 48,
              color: isLightColor(accent) ? '#1a202c' : '#ffffff',
              letterSpacing: 2,
            }}>
              {ageGroup || 'ALL AGES'}
            </span>
          </div>
        </div>

        {/* Details */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 24,
          flex: 1,
        }}>
          {(tryoutDate || tryoutDate2) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
              <div style={dotStyle} />
              <span style={{
                fontFamily: 'Barlow, sans-serif',
                fontSize: 36, fontWeight: 600, color: detailColor,
              }}>
                {tryoutDate ? formatGameDate(tryoutDate, dateFormat) : ''}
                {tryoutDate && tryoutDate2 ? ' & ' : ''}
                {tryoutDate2 ? formatGameDate(tryoutDate2, dateFormat) : ''}
              </span>
            </div>
          )}
          {tryoutTime && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
              <div style={dotStyle} />
              <span style={{
                fontFamily: 'Barlow, sans-serif',
                fontSize: 36, fontWeight: 600, color: detailColor,
              }}>
                {formatGameTime(tryoutTime)}
              </span>
            </div>
          )}
          {location && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
              <div style={dotStyle} />
              <span style={{
                fontFamily: 'Barlow, sans-serif',
                fontSize: 32, fontWeight: 600, color: detailColor,
              }}>
                {location}
              </span>
            </div>
          )}
          {additionalInfo && (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 20 }}>
              <div style={{ ...dotStyle, marginTop: 12 }} />
              <span style={{
                fontFamily: 'Barlow, sans-serif',
                fontSize: 28, fontWeight: 500,
                color: detailColor, opacity: 0.85,
                lineHeight: 1.4,
              }}>
                {additionalInfo}
              </span>
            </div>
          )}
        </div>

        {/* Contact strip */}
        {contact && (
          <div style={{
            background: 'rgba(0,0,0,0.6)',
            borderTop: `4px solid ${accent}`,
            padding: '24px 60px',
            marginLeft: -80,
            marginRight: -80,
            marginTop: 24,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 20,
          }}>
            <span style={{
              fontFamily: 'Barlow, sans-serif',
              fontSize: 28, fontWeight: 700,
              color: accent, letterSpacing: 2,
              textTransform: 'uppercase',
              flexShrink: 0,
            }}>
              Register:
            </span>
            <span style={{
              fontFamily: 'Barlow, sans-serif',
              fontSize: 28, fontWeight: 600, color: '#ffffff',
            }}>
              {contact}
            </span>
          </div>
        )}
      </div>

      {/* Watermark */}
      {showWatermark && (
        <div style={{
          position: 'absolute', bottom: 12, right: 12,
          background: 'rgba(255,255,255,0.92)', padding: '6px 12px',
          borderRadius: 8, pointerEvents: 'none', zIndex: 3,
        }}>
          <img src="/PMlogoleft.png" alt="Playmaker Media Kit" style={{ height: 80, width: 'auto', display: 'block' }} />
        </div>
      )}
    </div>
  );
}

export default GraphicTryoutPoster;
